<!--
CercleDigital InsiderGems, gestion électronique documentaire partagée de l'écosystème Cercle.
Copyright 2026 Pierre-Thibault Fabre <pierre-thibault@cercle.digital>
SPDX-License-Identifier: LicenseRef-Cercle-Interne

Fichier : apps/web/src/shell/Toasts.vue
Rôle : pile d'avis passagers, centrée en bas de l'écran.
Référence : designsystem.md ; CIG-SPECIFICATION.md §5.3.
-->
<script setup lang="ts">
import { useToasts, type Toast } from '../lib/toasts.ts';

const toasts = useToasts();

/** Part de délai restante, entre 0 et 1. C'est ce que montre la minuterie. */
function part(t: Toast): number {
  return Math.min(1, Math.max(0, t.restant / t.duree));
}
</script>

<template>
  <!--
    La zone vit en permanence, même vide : une région d'annonce créée en même
    temps que son contenu n'est pas lue par les technologies d'assistance.
  -->
  <div class="toasts" role="status">
    <TransitionGroup name="toast">
      <button
        v-for="t in toasts.messages"
        :key="t.id"
        type="button"
        class="toast"
        :class="`toast--${t.ton}`"
        title="Fermer cet avis"
        @click="toasts.retirer(t.id)"
        @pointerenter="toasts.suspendre(t.id, true)"
        @pointerleave="toasts.suspendre(t.id, false)"
        @focus="toasts.suspendre(t.id, true)"
        @blur="toasts.suspendre(t.id, false)"
      >
        <span class="texte">{{ t.texte }}</span>

        <!-- Un avis répété se recompte plutôt que de s'empiler : le décompte
             dit ce que la pile ne montre plus. -->
        <span v-if="t.repetitions > 1" class="repetitions">×{{ t.repetitions }}</span>

        <!--
          Minuterie. Elle occupe deux pixels au pied de l'avis : le délai
          restant se voit sans que rien ne clignote, et l'avis ne paraît pas
          disparaître de lui-même sans raison.
        -->
        <span class="minuterie" aria-hidden="true">
          <span class="jauge" :style="{ transform: `scaleX(${part(t)})` }" />
        </span>
      </button>
    </TransitionGroup>
  </div>
</template>

<style scoped>
/*
 * La pile se pose en bas au centre : c'est le seul angle de l'écran qu'aucune
 * commande n'occupe durablement, et le pouce l'atteint sur mobile. Elle passe
 * au-dessus de tout, visionneuse comprise, faute de quoi un échec de
 * téléchargement resterait invisible là où il se produit.
 */
.toasts {
  position: fixed;
  left: 50%;
  bottom: calc(var(--cig-espace-3) + env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  z-index: 95;
  display: flex;
  flex-direction: column;
  gap: var(--cig-espace-1);
  width: min(30rem, calc(100% - 2 * var(--cig-espace-3)));
  /* Le conteneur couvre une bande du bas de l'écran : il ne doit rien
     intercepter. Seuls les avis eux-mêmes reçoivent le pointeur. */
  pointer-events: none;
}

/*
 * Un avis est un bouton : cliquer dessus le referme, et c'est sa seule action.
 * Une croix supplémentaire ajouterait une cible de 24 pixels à viser là où
 * l'avis tout entier fait déjà l'affaire, au doigt comme à la souris.
 */
.toast {
  position: relative;
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: var(--cig-espace-2);
  width: 100%;
  min-height: var(--cig-touche);
  padding: var(--cig-espace-2) var(--cig-espace-3);
  overflow: hidden;
  border-radius: var(--cig-rayon-petit);
  background: var(--cig-contraste);
  color: var(--cig-contraste-encre);
  font-size: 0.86rem;
  font-weight: 500;
  line-height: 1.4;
  text-align: left;
  /*
   * L'ombre de la charte est froide et claire : elle détache l'avis du blanc
   * de la zone documentaire. Le liseré s'en charge sur fond sombre — la
   * visionneuse — où l'ombre ne se voit pas.
   */
  box-shadow: var(--cig-ombre-haute), 0 0 0 1px rgb(255 255 255 / 14%);
}

.toast:hover {
  /* La charte estompe les boutons au survol ; un avis que l'on survole est un
     avis que l'on lit, il doit au contraire se détacher davantage. */
  opacity: 1;
  box-shadow: var(--cig-ombre-haute), 0 0 0 1px rgb(255 255 255 / 32%);
}
.toast:focus-visible { outline: 2px solid var(--cig-contraste-encre); outline-offset: 2px; }

/* Filet de ton, sur toute la hauteur : la nature de l'avis se lit tant qu'il
   est à l'écran, là où la minuterie, elle, s'épuise. */
.toast::before {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: var(--cig-contraste-gris);
}

.texte { flex: 1; min-width: 0; overflow-wrap: anywhere; }

.repetitions {
  flex: none;
  font-size: 0.74rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--cig-contraste-gris);
}

.minuterie {
  position: absolute;
  inset: auto 0 0 0;
  height: 2px;
  background: var(--cig-contraste-filet);
}
.jauge {
  display: block;
  height: 100%;
  transform-origin: left;
  background: var(--cig-contraste-gris);
}

/*
 * Couleur des tons. Elle est rare et n'ajoute rien au texte : celui-ci dit
 * déjà ce qui s'est passé.
 */
.toast--succes::before, .toast--succes .jauge { background: var(--cig-contraste-valide); }
.toast--alerte::before, .toast--alerte .jauge { background: var(--cig-contraste-alerte); }
.toast--danger::before, .toast--danger .jauge { background: var(--cig-contraste-danger); }

/* L'avis monte depuis le bas, d'où il vient, et les autres lui font place. */
.toast-enter-active { transition: opacity var(--cig-duree-courte) ease, transform var(--cig-duree) var(--cig-elan); }
.toast-leave-active { transition: opacity var(--cig-duree-courte) ease, transform var(--cig-duree-courte) ease; }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: translateY(0.6rem); }
.toast-move { transition: transform var(--cig-duree) var(--cig-elan); }

/* Sur écran étroit, l'avis prend toute la largeur utile : le texte y respire. */
@media (max-width: 40rem) {
  .toasts {
    width: calc(100% - 2 * var(--cig-espace-2));
    bottom: calc(var(--cig-espace-2) + env(safe-area-inset-bottom, 0px));
  }
  .toast { font-size: 0.84rem; }
}
</style>
